import { createRouter, createWebHistory } from 'vue-router'
import { trackPageView } from '@/core/matomo'
import { useAuthStore, type OfficeDeskPermission } from '@/stores/auth/store'
import { useNetworkStore } from '@/stores/network/store'
import { useUiStore } from '@/stores/ui/store'
import { useCtaStore } from '@/stores/cta/store'
import { useFeaturesStore } from '@/stores/features/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { isTesting, setEkpOrigin } from '@/utils/environment'
import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
} from 'vue-router'
import { loginKeycloakSso, redirectToKeycloakLogin } from '@/core/keycloak'
import { getMsLoginFeatureRedirect, parseNumberParam } from './utils'
import { useNavigationStore } from '@/stores/navigation/store'
import { storage } from '@/utils/storage/storage'

const history = createWebHistory()

export type BreadCrumb = {
  name: string
  params?: (currentRoute: RouteLocationNormalizedLoaded) => any
}

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    breadcrumb?: BreadCrumb[]
    isForbiddenForOfficeDesk?: boolean
    requiredOfficeDeskPermission?: OfficeDeskPermission
    notAvailableForBasic?: boolean
    notAvailableForClassic?: boolean
    requiresAccessToExtension?: (
      to: RouteLocationNormalized
    ) => number | undefined
    requiresAccessToDevice?: (to: RouteLocationNormalized) => number | undefined
  }
}

const router = createRouter({
  history,
  routes: [
    {
      path: '/ms-login-proxy',
      component: { template: '<div></div>' },
    },
    {
      // EKP SSO entry point for Keycloak-authenticated customers
      path: '/sso/keycloak/:path(.*)*',
      redirect: (to) => {
        // strip leading slashes so the target can never become protocol-relative (//host)
        const targetPath = [to.params.path ?? []]
          .flat()
          .join('/')
          .replace(/^\/+/, '')

        return {
          path: `/${targetPath}`,
          query: { ...to.query, loginWith: 'keycloak' },
          hash: to.hash,
        }
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import('@/views/Logout.vue'),
    },
    {
      path: '/',
      name: 'overview',
      component: () => import('@/views/Overview.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/settings/Settings.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'settings',
          },
        ],
        requiresAuth: true,
        requiredPageData: ['fetchPbx', 'loadExtensions'],
        isForbiddenForOfficeDesk: true,
      },
      props: true,
    },
    {
      path: '/extensions/new',
      name: 'extension.new',
      component: () => import('@/views/extensions/Extension.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.new',
          },
        ],
        requiresAuth: true,
        isForbiddenForOfficeDesk: true,
      },
    },
    {
      path: '/extensions/:extensionId',
      name: 'extension.edit',
      component: () => import('@/views/extensions/Extension.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extensions/:extensionId/extended-forwarding/profile/:profileId/step/add',
      name: 'profile.addstep',
      component: () =>
        import('@/views/extensions/forwarding/extended/AddEditStep.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
          {
            name: 'profile.edit',
          },
          {
            name: 'profile.addstep',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extensions/:extensionId/extended-forwarding/profile/:profileId/step/:stepId',
      name: 'profile.editstep',
      component: () =>
        import('@/views/extensions/forwarding/extended/AddEditStep.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
          {
            name: 'profile.edit',
          },
          {
            name: 'profile.editstep',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extensions/:extensionId/extended-forwarding/profile/add',
      name: 'profile.add',
      component: () =>
        import('@/views/extensions/forwarding/extended/AddEditProfile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
          {
            name: 'extendedforwaring',
          },
          {
            name: 'profile.add',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extensions/:extensionId/extended-forwarding/profile/:profileId',
      name: 'profile.edit',
      component: () =>
        import('@/views/extensions/forwarding/extended/AddEditProfile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
          {
            name: 'extendedforwaring',
          },
          {
            name: 'profile.edit',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extensions/:extensionId/extended-forwarding',
      name: 'extendedforwaring',
      component: () =>
        import('@/views/extensions/forwarding/extended/ExtendedForwarding.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
          {
            name: 'extendedforwaring',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extensions/:extensionId/forwarding',
      name: 'simpleforwarding',
      component: () =>
        import('@/views/extensions/forwarding/simple/SimpleForwarding.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
          {
            name: 'simpleforwarding',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extensions/:extensionId/nightswitch',
      name: 'nightswitch',
      component: () => import('@/views/NightSwitch.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
          {
            name: 'nightswitch',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extensions/:extensionId/callflow',
      name: 'callflowcreator',
      component: () => import('@/views/extensions/CallFlowCreator.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.edit',
          },
          {
            name: 'callflowcreator',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.extensionId),
      },
    },
    {
      path: '/extension-multi-edit',
      props: true,
      name: 'extension.multiedit',
      component: () => import('@/views/extensions/ExtensionMultiEdit.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'extension.multiedit',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/devices/new',
      name: 'device.add',
      component: () => import('@/views/devices/Device.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'device.add',
          },
        ],
        requiresAuth: true,
        isForbiddenForOfficeDesk: true,
      },
    },
    {
      path: '/devices/:deviceId',
      name: 'device.edit',
      component: () => import('@/views/devices/Device.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'device.edit',
          },
        ],
        requiresAuth: true,
        requiresAccessToDevice: (to) => parseNumberParam(to.params.deviceId),
      },
    },
    {
      path: '/device-multi-edit',
      props: true,
      name: 'device.multiedit',
      component: () => import('@/views/devices/DeviceMultiEdit.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'device.multiedit',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/office-desks/new',
      name: 'officedesk.add',
      component: () => import('@/views/officeDesks/OfficeDesk.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'officedesk.add',
          },
        ],
        requiresAuth: true,
        isForbiddenForOfficeDesk: true,
        notAvailableForBasic: true,
      },
    },
    {
      path: '/office-desks/:officeDeskId',
      name: 'officedesk.edit',
      component: () => import('@/views/officeDesks/OfficeDesk.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'officedesk.edit',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/office-desks-multi',
      props: true,
      name: 'officedesk.multiedit',
      component: () => import('@/views/officeDesks/OfficeDeskMultiEdit.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'officedesk.multiedit',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/devices/:deviceId/password',
      name: 'device.password',
      component: () => import('@/views/devices/DevicePassword.vue'),
      meta: {
        requiresAuth: true,
        breadcrumb: [
          {
            name: 'device.edit',
            params: (currentRoute) => ({
              deviceId: currentRoute.params.deviceId,
            }),
          },
          {
            name: 'device.password',
          },
        ],
        requiresAccessToDevice: (to) => parseNumberParam(to.params.deviceId),
      },
    },
    {
      path: '/devices/:deviceId/allowed-ips',
      name: 'device.allowed-ips',
      component: () => import('@/views/devices/AllowedIps.vue'),
      meta: {
        requiresAuth: true,
        breadcrumb: [
          {
            name: 'device.edit',
          },
          {
            name: 'device.allowed-ips',
          },
        ],
        requiresAccessToDevice: (to) => parseNumberParam(to.params.deviceId),
      },
    },
    {
      path: '/devices/:deviceId/softkeys',
      name: 'device.softkeys',
      component: () => import('@/views/devices/Softkeys/Softkeys.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'device.edit',
          },
          {
            name: 'device.softkeys',
          },
        ],
        requiresAuth: true,
        requiresAccessToDevice: (to) => parseNumberParam(to.params.deviceId),
      },
    },
    {
      path: '/audio-files/new',
      name: 'audiofiles.new',
      component: () => import('@/views/prompts/Prompt.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'audiofiles.new',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/audio-files/record/new',
      name: 'audiofiles.record.new',
      component: () => import('@/views/prompts/RecordPrompt.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'audiofiles.record.new',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/:audioType/:audioId',
      name: 'audiofiles.edit',
      component: () => import('@/views/prompts/Prompt.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'audiofiles.edit',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/:audioType/:audioId/record',
      name: 'audiofiles.record',
      component: () => import('@/views/prompts/RecordPrompt.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'audiofiles.record',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/contacts',
      name: 'contacts',
      component: () => import('@/views/contacts/Contacts.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'contacts',
          },
        ],
        requiresAuth: true,
        requiredOfficeDeskPermission: 'contactsPermission',
      },
    },
    {
      path: '/password',
      name: 'password',
      component: () => import('@/views/Password.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'password',
          },
        ],
      },
    },
    // UCA - users
    {
      path: '/uca/users',
      name: 'uca-users',
      component: () => import('@/views/uca/Users.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/users/new',
      name: 'uca-users-new',
      component: () => import('@/views/uca/users/User.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-new',
          },
        ],
        requiresAuth: true,
        isForbiddenForOfficeDesk: true,
      },
    },
    {
      path: '/uca/users/:userId',
      name: 'uca-users-edit',
      component: () => import('@/views/uca/users/User.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-edit',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.userId),
      },
    },
    {
      path: '/uca/users/:userId/callflow',
      name: 'uca-users-callflow',
      component: () => import('@/views/extensions/CallFlowCreator.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-user',
          },
          {
            name: 'uca-users-callflow',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.userId),
      },
    },
    {
      path: '/uca/users/:userId/nightswitch',
      name: 'uca-users-nightswitch',
      component: () => import('@/views/uca/users/NightSwitch.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-edit',
          },
          {
            name: 'uca-users-nightswitch',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
      },
    },
    {
      path: '/uca/users/:userId/forwarding',
      name: 'uca-user-simple-forwarding',
      component: () =>
        import('@/views/uca/users/forwarding/simple/SimpleForwarding.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-edit',
          },
          {
            name: 'uca-user-simple-forwarding',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.userId),
      },
    },
    {
      path: '/uca/users/:userId/extended-forwarding',
      name: 'uca-user-extended-forwarding',
      component: () =>
        import('@/views/uca/users/forwarding/extended/ExtendedForwarding.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-edit',
          },
          {
            name: 'uca-user-extended-forwarding',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.userId),
      },
    },
    {
      path: '/uca/users/:userId/extended-forwarding/profile/add',
      name: 'uca-user-profile-add',
      component: () =>
        import('@/views/uca/users/forwarding/extended/AddEditProfile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-edit',
          },
          {
            name: 'uca-user-profile-add',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.userId),
      },
    },
    {
      path: '/uca/users/:userId/extended-forwarding/profile/:profileId',
      name: 'uca-user-profile-edit',
      component: () =>
        import('@/views/uca/users/forwarding/extended/AddEditProfile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-edit',
          },
          {
            name: 'uca-user-profile-edit',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.userId),
      },
    },
    {
      path: '/uca/users/:userId/extended-forwarding/profile/:profileId/step/add',
      name: 'uca-user-profile-step-add',
      component: () =>
        import('@/views/uca/users/forwarding/extended/AddEditStep.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-edit',
          },
          {
            name: 'uca-user-profile-edit',
          },
          {
            name: 'uca-user-profile-step-add',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.userId),
      },
    },
    {
      path: '/uca/users/:userId/extended-forwarding/profile/:profileId/step/:stepId',
      name: 'uca-user-profile-step-edit',
      component: () =>
        import('@/views/uca/users/forwarding/extended/AddEditStep.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-edit',
          },
          {
            name: 'uca-user-profile-edit',
          },
          {
            name: 'uca-user-profile-step-edit',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.userId),
      },
    },
    {
      path: '/uca/users/multi-edit',
      props: true,
      name: 'uca-users-multi-edit',
      component: () => import('@/views/uca/users/UserMultiEdit.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-users',
          },
          {
            name: 'uca-users-multi-edit',
          },
        ],
        requiresAuth: true,
      },
    },
    // UCA - resources
    {
      path: '/uca/resources',
      name: 'uca-resources',
      component: () => import('@/views/uca/Resources.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/resources/new',
      name: 'uca-resources-new',
      component: () => import('@/views/uca/resources/Resource.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-new',
          },
        ],
        requiresAuth: true,
        isForbiddenForOfficeDesk: true,
      },
    },
    {
      path: '/uca/resources/:resourceId',
      name: 'uca-resources-edit',
      component: () => import('@/views/uca/resources/Resource.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId',
      name: 'uca-workspace-edit',
      component: () => import('@/views/uca/resources/Resource.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-workspace-edit',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId/forwarding',
      name: 'uca-resource-simple-forwarding',
      component: () =>
        import('@/views/uca/users/forwarding/simple/SimpleForwarding.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
          {
            name: 'uca-resource-simple-forwarding',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId/extended-forwarding',
      name: 'uca-resource-extended-forwarding',
      component: () =>
        import('@/views/uca/users/forwarding/extended/ExtendedForwarding.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
          {
            name: 'uca-resource-extended-forwarding',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId/extended-forwarding/profile/add',
      name: 'uca-resource-profile-add',
      component: () =>
        import('@/views/uca/users/forwarding/extended/AddEditProfile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
          {
            name: 'uca-resource-profile-add',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId/extended-forwarding/profile/:profileId',
      name: 'uca-resource-profile-edit',
      component: () =>
        import('@/views/uca/users/forwarding/extended/AddEditProfile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
          {
            name: 'uca-resource-profile-edit',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId/extended-forwarding/profile/:profileId/step/add',
      name: 'uca-resource-profile-step-add',
      component: () =>
        import('@/views/uca/users/forwarding/extended/AddEditStep.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
          {
            name: 'uca-resource-profile-edit',
          },
          {
            name: 'uca-resource-profile-step-add',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId/extended-forwarding/profile/:profileId/step/:stepId',
      name: 'uca-resource-profile-step-edit',
      component: () =>
        import('@/views/uca/users/forwarding/extended/AddEditStep.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
          {
            name: 'uca-resource-profile-edit',
          },
          {
            name: 'uca-resource-profile-step-edit',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId/nightswitch',
      name: 'uca-resources-nightswitch',
      component: () => import('@/views/uca/users/NightSwitch.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
          {
            name: 'uca-nightswitch',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/:resourceId/callflow',
      name: 'uca-resources-callflow',
      component: () => import('@/views/extensions/CallFlowCreator.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-edit',
          },
          {
            name: 'uca-callflow',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        requiresAccessToExtension: (to) =>
          parseNumberParam(to.params.resourceId),
      },
    },
    {
      path: '/uca/resources/multi-edit',
      props: true,
      name: 'uca-resources-multi-edit',
      component: () => import('@/views/uca/resources/ResourceMultiEdit.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-resources',
          },
          {
            name: 'uca-resources-multi-edit',
          },
        ],
        requiresAuth: true,
      },
    },
    // UCA - devices
    {
      path: '/uca/devices',
      name: 'uca-devices',
      component: () => import('@/views/uca/Devices.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-devices',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/devices/new',
      name: 'uca-device-add',
      component: () => import('@/views/uca/devices/Device.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-devices',
          },
          {
            name: 'uca-device-add',
          },
        ],
        requiresAuth: true,
        isForbiddenForOfficeDesk: true,
      },
    },
    {
      path: '/uca/devices/:deviceId',
      name: 'uca-device-edit',
      component: () => import('@/views/uca/devices/Device.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-devices',
          },
          {
            name: 'uca-device-edit',
          },
        ],
        requiresAuth: true,
        requiresAccessToDevice: (to) => parseNumberParam(to.params.deviceId),
      },
    },
    {
      path: '/uca/devices/multi-edit',
      name: 'uca-device-multiedit',
      props: true,
      component: () => import('@/views/uca/devices/DeviceMultiEdit.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-devices',
          },
          {
            name: 'uca-device-multiedit',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/devices/:deviceId/password',
      name: 'uca-device-password',
      component: () => import('@/views/uca/devices/DevicePassword.vue'),
      meta: {
        requiresAuth: true,
        breadcrumb: [
          {
            name: 'uca-devices',
          },
          {
            name: 'uca-device-edit',
            params: (currentRoute: RouteLocationNormalizedLoaded) => ({
              deviceId: currentRoute.params.deviceId,
            }),
          },
          {
            name: 'uca-device-password',
          },
        ],
        requiresAccessToDevice: (to) => parseNumberParam(to.params.deviceId),
      },
    },
    {
      path: '/uca/devices/:deviceId/allowed-ips',
      name: 'uca-device-allowed-ips',
      component: () => import('@/views/uca/devices/AllowedIps.vue'),
      meta: {
        requiresAuth: true,
        breadcrumb: [
          {
            name: 'uca-devices',
          },
          {
            name: 'uca-device-edit',
          },
          {
            name: 'uca-device-allowed-ips',
          },
        ],
      },
    },
    {
      path: '/uca/devices/:deviceId/softkeys',
      name: 'uca-device-softkeys',
      component: () => import('@/views/uca/devices/Softkeys/Softkeys.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-devices',
          },
          {
            name: 'uca-device-edit',
          },
          {
            name: 'uca-device-softkeys',
          },
        ],
        requiresAuth: true,
        requiresAccessToDevice: (to) => parseNumberParam(to.params.deviceId),
      },
    },
    // UCA - groups
    {
      path: '/uca/groups',
      name: 'uca-groups',
      component: () => import('@/views/uca/Groups.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-groups',
          },
        ],
        requiresAuth: true,
        notAvailableForBasic: true,
        notAvailableForClassic: true,
      },
    },
    {
      path: '/uca/groups/new',
      name: 'uca-group-add',
      component: () => import('@/views/uca/groups/Group.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-groups',
          },
          {
            name: 'uca-group-add',
          },
        ],
        requiresAuth: true,
        isForbiddenForOfficeDesk: true,
        notAvailableForBasic: true,
        notAvailableForClassic: true,
      },
    },
    {
      path: '/uca/groups/:groupId',
      name: 'uca-group-edit',
      component: () => import('@/views/uca/groups/Group.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-groups',
          },
          {
            name: 'uca-group-edit',
          },
        ],
        requiresAuth: true,
        requiresAccessToExtension: (to) => parseNumberParam(to.params.groupId),
        notAvailableForBasic: true,
        notAvailableForClassic: true,
      },
    },
    // UCA - audio files
    {
      path: '/uca/audio-files',
      name: 'uca-audio-files',
      component: () => import('@/views/uca/AudioFiles.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-audio-files',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/audio-files/new',
      name: 'uca-audiofiles-new',
      component: () => import('@/views/uca/audioFiles/AudioFile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-audio-files',
          },
          {
            name: 'uca-audiofiles-new',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/audio-files/record/new',
      name: 'uca-audiofiles-record-new',
      component: () => import('@/views/uca/audioFiles/AudioFile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-audio-files',
          },
          {
            name: 'uca-audiofiles-record-new',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/audio-files/:audioType/:audioId',
      name: 'uca-audiofiles-edit',
      component: () => import('@/views/uca/audioFiles/AudioFile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-audio-files',
          },
          {
            name: 'uca-audiofiles-edit',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/audio-files/:audioType/:audioId/record',
      name: 'uca-audiofiles-record',
      component: () => import('@/views/uca/audioFiles/AudioFile.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-audio-files',
          },
          {
            name: 'uca-audiofiles-record',
          },
        ],
        requiresAuth: true,
      },
    },
    // UCA - office desks
    {
      path: '/uca/office-desks',
      name: 'uca-office-desks',
      component: () => import('@/views/uca/OfficeDesks.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-office-desks',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/office-desks/new',
      name: 'uca-office-desk-add',
      component: () => import('@/views/uca/officeDesks/OfficeDesk.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-office-desks',
          },
          {
            name: 'uca-office-desk-add',
          },
        ],
        requiresAuth: true,
        isForbiddenForOfficeDesk: true,
        notAvailableForBasic: true,
      },
    },
    {
      path: '/uca/office-desks/:officeDeskId',
      name: 'uca-office-desk-edit',
      component: () => import('@/views/uca/officeDesks/OfficeDesk.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-office-desks',
          },
          {
            name: 'uca-office-desk-edit',
          },
        ],
        requiresAuth: true,
      },
    },
    {
      path: '/uca/office-desks/multi-edit',
      name: 'uca-office-desk-multi-edit',
      props: true,
      component: () =>
        import('@/views/uca/officeDesks/OfficeDeskMultiEdit.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-office-desks',
          },
          {
            name: 'uca-office-desk-multi-edit',
          },
        ],
        requiresAuth: true,
      },
    },
    // UCA - others
    {
      path: '/uca/settings',
      name: 'uca-settings',
      component: () => import('@/views/uca/settings/Settings.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-settings',
          },
        ],
        requiresAuth: true,
        requiredPageData: ['fetchPbx', 'loadExtensions'],
        isForbiddenForOfficeDesk: true,
      },
      props: true,
    },
    {
      path: '/uca/contacts',
      name: 'uca-contacts',
      component: () => import('@/views/uca/contacts/Contacts.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-contacts',
          },
        ],
        requiresAuth: true,
        requiredOfficeDeskPermission: 'contactsPermission',
      },
    },
    {
      path: '/uca/password',
      name: 'uca-password',
      component: () => import('@/views/uca/password/Password.vue'),
      meta: {
        breadcrumb: [
          {
            name: 'uca-password',
          },
        ],
      },
    },
    {
      // Equivalent to path: '*' in Vue 2. This will pass the url as a param: (/wrong/path => $route.params.path = "wrong/path")
      path: '/:path(.*)',
      redirect: '/',
    },
  ],
})

function removeQueryParams(to: RouteLocationNormalized, paramKeys: string[]) {
  const query = { ...to.query }

  for (const key of paramKeys) {
    delete query[key]
  }

  return { path: to.path, query, hash: to.hash }
}

function isUcaUserDetailRoute(to: RouteLocationNormalized) {
  return (
    to.path.toLowerCase().startsWith('/uca/users/') &&
    !!parseNumberParam(to.params.userId)
  )
}

function isUcaResourceDetailRoute(to: RouteLocationNormalized) {
  return (
    to.path.toLowerCase().startsWith('/uca/resources/') &&
    !!parseNumberParam(to.params.resourceId)
  )
}

function getUcaEntityRouteType(to: RouteLocationNormalized) {
  if (isUcaUserDetailRoute(to)) {
    return 'user'
  }

  if (isUcaResourceDetailRoute(to)) {
    return 'resource'
  }
}

function getUcaEntityId(to: RouteLocationNormalized) {
  return (
    parseNumberParam(to.params.userId) ?? parseNumberParam(to.params.resourceId)
  )
}

function getMappedUcaEntityRoute(
  to: RouteLocationNormalized,
  targetType: 'user' | 'resource'
) {
  const extensionId = getUcaEntityId(to)

  if (!extensionId) {
    return undefined
  }

  const sourceBase = isUcaUserDetailRoute(to)
    ? '/uca/users/'
    : '/uca/resources/'
  const targetBase = targetType === 'user' ? '/uca/users/' : '/uca/resources/'

  return {
    path: to.path
      .toLowerCase()
      .replace(sourceBase + extensionId, targetBase + extensionId),
  }
}

router.beforeEach(async (to) => {
  if (to.path.toLowerCase() === '/ms-login-proxy') {
    const redirectOrigin = getMsLoginFeatureRedirect(
      location.hash,
      window.location.origin
    )

    if (!redirectOrigin) {
      return { path: '/', replace: true }
    }

    window.location.href = redirectOrigin + location.hash
    return false
  }

  trackPageView()

  // * Clear blockActions
  useUiStore().stopBlockingActions()

  const originQuery = to.query.origin

  if (Array.isArray(originQuery)) {
    if (originQuery[0]) {
      setEkpOrigin(originQuery[0])
    }
  } else if (typeof originQuery === 'string' && originQuery) {
    setEkpOrigin(originQuery)
  }

  // #### HANDLE AUTH QUERIES
  if (to.query.customerId) {
    const loginResult = await useAuthStore().login({
      value: String(to.query.customerId),
      mode: 'customerId',
    })

    if (loginResult === 'error') {
      return false
    }

    return removeQueryParams(to, ['customerId', 'origin'])
  } else if (to.query.auth) {
    const loginResult = await useAuthStore().login({
      value: String(to.query.auth),
    })

    if (loginResult === 'error') {
      return false
    }

    return removeQueryParams(to, ['auth', 'origin'])
  } else if (to.query.loginWith === 'keycloak') {
    // persist early so isKeycloakUsed() is true if /whoami returns 401 during login
    storage.authMode = 'keycloak'

    const token = await loginKeycloakSso()

    if (!token) {
      redirectToKeycloakLogin()
      return false
    }

    const loginResult = await useAuthStore().login({ value: token })

    if (loginResult === 'error') {
      return false
    }

    return removeQueryParams(to, ['loginWith', 'origin'])
  }

  // #### HANDLE AUTHENTIFICATION
  if (!useAuthStore().isLoggedIn) {
    // * Check if pbxExtension is stored inaut LocalStorage and login the pbx if it is present
    await useAuthStore().checkLocalStorageForLogin()
  }

  if (useAuthStore().ctaId) {
    await useFeaturesStore().load()
    await useNavigationStore().load()
  }

  if (to.path.toLowerCase() === '/login' && !isTesting()) {
    // * If app is integrated with EKP, redirect to EKP
    useAuthStore().goToLogin()

    return false
  }

  if (to.path.toLowerCase() === '/logout') {
    return true
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    // * If user is not logged in, redirect to login page
    if (!useAuthStore().isLoggedIn) {
      useAuthStore().goToLogin()

      return false
    }

    if (to.meta.isForbiddenForOfficeDesk && useAuthStore().isOfficeDesk) {
      useAuthStore().goToEkp()

      return false
    }

    if (to.meta.requiredOfficeDeskPermission) {
      if (
        useAuthStore().isOfficeDesk &&
        !useAuthStore().getHasOfficeDeskPermission(
          to.meta.requiredOfficeDeskPermission
        )
      ) {
        useAuthStore().goToEkp()

        return false
      }
    }

    if (to.meta.requiresAccessToExtension) {
      if (
        useAuthStore().isOfficeDesk &&
        !useAuthStore().getHasOfficeDeskAccessToExtension(
          to.meta.requiresAccessToExtension(to)!
        )
      ) {
        useAuthStore().goToEkp()

        return false
      }
    }

    if (to.meta.requiresAccessToDevice) {
      if (
        useAuthStore().isOfficeDesk &&
        !useAuthStore().getHasOfficeDeskAccessToDevice(
          to.meta.requiresAccessToDevice(to)!
        )
      ) {
        useAuthStore().goToEkp()

        return false
      }
    }

    const ucaEntityRouteType = getUcaEntityRouteType(to)

    if (ucaEntityRouteType) {
      const extensionsStore = useExtensionsStore()

      await extensionsStore.load()

      const extensionId = getUcaEntityId(to)
      const extension = extensionId
        ? extensionsStore.find(extensionId)
        : undefined

      if (extension) {
        const isAllowedOnUserRoute = ['user', 'default'].includes(
          extension.type
        )
        const isAllowedOnResourceRoute = !isAllowedOnUserRoute

        if (ucaEntityRouteType === 'user' && !isAllowedOnUserRoute) {
          if (isAllowedOnResourceRoute) {
            return getMappedUcaEntityRoute(to, 'resource')
          }

          return { path: '/uca/users' }
        }

        if (ucaEntityRouteType === 'resource' && !isAllowedOnResourceRoute) {
          if (isAllowedOnUserRoute) {
            return getMappedUcaEntityRoute(to, 'user')
          }

          return { path: '/uca/resources' }
        }
      }
    }

    //redirect if basic or classic
    const isNotAvailableForBasic = to.matched.some(
      (record) => record.meta.notAvailableForBasic
    )
    const isNotAvailableForClassic = to.matched.some(
      (record) => record.meta.notAvailableForClassic
    )

    if (isNotAvailableForBasic || isNotAvailableForClassic) {
      await useCtaStore().load()

      if (
        (useCtaStore().getEdition === 'basic' && isNotAvailableForBasic) ||
        (useCtaStore().getEdition === 'standard' && isNotAvailableForClassic)
      ) {
        useAuthStore().goToEkp()

        return false
      }
    }
  }

  const isUcaPage = to.fullPath.toLowerCase().includes('/uca/')

  const isLogoutPage =
    to.fullPath.toLowerCase().includes('/login') ||
    to.fullPath.toLowerCase().includes('/logout')

  if (!isLogoutPage) {
    // Redirect to overview if is not allowed in UCA pages
    if (isUcaPage && !useFeaturesStore().isUcaMode) {
      router.push('/')
    }

    // Redirect to UCA users if is only allowed in UCA pages
    if (!isUcaPage && useFeaturesStore().isUcaMode) {
      if (to.path.toLowerCase() === '/password') {
        return { path: '/uca/password', query: to.query }
      }
      if (to.path.toLowerCase() === '/settings') {
        return { path: '/uca/settings', query: to.query }
      } else {
        router.push('/uca/users')
      }
    }
  }

  return true
})

router.afterEach(() => {
  setTimeout(() => {
    // * Stop showing loading animation
    useNetworkStore().setPageLoading(false)
  }, 0)
})

export default router
